import {
	routes,
	type NotificationSettingsView,
	type NotificationsResponse,
	type UpdateNotificationSettingsRequest,
} from '@spot/shared';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useSession } from '../auth/session-gate';

const listKey = ['me', 'notifications'] as const;
const settingsKey = ['me', 'notification-settings'] as const;

/** S15 center: newest first; each page also carries the current unread count. */
export function useNotifications() {
	const { api } = useSession();
	return useInfiniteQuery({
		queryKey: listKey,
		queryFn: ({ pageParam }) =>
			api.call(routes.myNotifications, {
				query: pageParam === undefined ? {} : { cursor: pageParam },
			}),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (page: NotificationsResponse) => page.nextCursor ?? undefined,
		staleTime: 30_000,
	});
}

/** Marks everything read; the open list keeps its dots until the next refetch. */
export function useMarkNotificationsRead() {
	const { api } = useSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => api.call(routes.markNotificationsRead),
		onSuccess: ({ unreadCount }) => {
			queryClient.setQueryData<{ pages: NotificationsResponse[]; pageParams: unknown[] }>(
				listKey,
				(data) =>
					data === undefined
						? data
						: { ...data, pages: data.pages.map((page) => ({ ...page, unreadCount })) },
			);
		},
	});
}

export function useNotificationSettings() {
	const { api } = useSession();
	return useQuery<NotificationSettingsView>({
		queryKey: settingsKey,
		queryFn: () => api.call(routes.notificationSettings),
	});
}

/** Optimistic: the switch flips at once and rolls back if the save fails. */
export function useUpdateNotificationSettings() {
	const { api } = useSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: UpdateNotificationSettingsRequest) =>
			api.call(routes.updateNotificationSettings, { body }),
		onMutate: async (body) => {
			await queryClient.cancelQueries({ queryKey: settingsKey });
			const before = queryClient.getQueryData<NotificationSettingsView>(settingsKey);
			if (before !== undefined) {
				queryClient.setQueryData<NotificationSettingsView>(settingsKey, {
					types: { ...before.types, ...body.types },
					quietHours: body.quietHours ?? before.quietHours,
				});
			}
			return { before };
		},
		onError: (_error, _body, context) => {
			if (context?.before !== undefined) queryClient.setQueryData(settingsKey, context.before);
		},
		onSuccess: (view) => {
			queryClient.setQueryData(settingsKey, view);
		},
	});
}
